import { useEvent } from '@gemini/fe-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FIELD_ERROR_SCROLL_SELECTOR } from '@common/FieldScroll';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import {
  IContinueAttemptEvent,
  IIncompletePatientsCheckEvent,
  OBEvents,
} from '@utils/events/events';

const DEFAULT_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'center',
};

const MAX_SCROLL_LOOKUP_ATTEMPTS = 8;
const ERROR_SCROLL_SELECTOR = [
  FIELD_ERROR_SCROLL_SELECTOR,
  '[aria-invalid="true"]',
  '.Mui-error input',
  '.Mui-error textarea',
  '.Mui-error [role="combobox"]',
  '.MuiFormHelperText-root.Mui-error',
].join(', ');

interface UseContinueValidationFeedbackOptions {
  /** Step handled by this hook instance; attempts from other steps are ignored. */
  step: StepsStep;
  /** Allows multiple instances for the same step while activating only the current branch. */
  enabled?: boolean;
  /** Clears submit attempts when the provided condition becomes true, usually after validation passes. */
  resetWhen?: boolean;
  /** Runs step-specific side effects, such as setting local error flags or snapshot refs. */
  onAttempt?: (payload: IContinueAttemptEvent) => void;
  /** Returns incomplete patient display names for the shared multipatient feedback event. */
  getIncompletePatientNames?: () => string[];
  /** Overrides the default scrollIntoView options. */
  scrollOptions?: ScrollIntoViewOptions;
}

interface UseContinueValidationFeedbackResult {
  /** Count passed to child forms so they reveal/trigger validation errors after Continue. */
  submitAttemptCount: number;
  /** Clears validation attempts and the internal scroll de-duplication marker. */
  resetSubmitAttemptCount: () => void;
}

const isHiddenForScroll = (element: HTMLElement) => {
  let currentElement: HTMLElement | null = element;

  while (currentElement && currentElement !== document.body) {
    if (
      currentElement.hidden ||
      currentElement.getAttribute('aria-hidden') === 'true'
    ) {
      return true;
    }

    const style = globalThis.getComputedStyle(currentElement);

    if (style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }

    currentElement = currentElement.parentElement;
  }

  return false;
};

const getErrorScrollTarget = (errorElement: HTMLElement) =>
  errorElement.closest<HTMLElement>(
    FIELD_ERROR_SCROLL_SELECTOR + ', .MuiFormControl-root',
  ) || errorElement;

const getFirstErrorScrollTarget = () => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const errorElements = Array.from(
    document.querySelectorAll<HTMLElement>(ERROR_SCROLL_SELECTOR),
  );

  const firstVisibleError = errorElements.find(
    errorElement => !isHiddenForScroll(errorElement),
  );

  return firstVisibleError
    ? getErrorScrollTarget(firstVisibleError)
    : undefined;
};

/**
 * Coordinates invalid Continue feedback for an online booking step.
 *
 * The hook listens for the shared Continue attempt event, filters it by step,
 * increments a submit-attempt counter, optionally emits the incomplete-patients
 * event, runs step-specific side effects, and scrolls to the first visible
 * validation error rendered on the page.
 *
 * @param options Hook configuration.
 * @param options.step Step handled by this hook instance.
 * @param options.enabled Whether this instance should react to Continue attempts. Defaults to true.
 * @param options.resetWhen When true, resets the submit-attempt counter.
 * @param options.onAttempt Optional callback for step-specific side effects.
 * @param options.getIncompletePatientNames Optional selector for names emitted through incompletePatientsCheck.
 * @param options.scrollOptions Optional scrollIntoView options. Defaults to smooth center scrolling.
 * @returns The current submit-attempt count and a manual reset callback.
 */
const useContinueValidationFeedback = (
  options: UseContinueValidationFeedbackOptions,
): UseContinueValidationFeedbackResult => {
  const {
    step,
    enabled = true,
    resetWhen = false,
    onAttempt,
    getIncompletePatientNames,
    scrollOptions = DEFAULT_SCROLL_OPTIONS,
  } = options;

  const { emit, subscribe } = useEvent();
  const [submitAttemptCount, setSubmitAttemptCount] = useState(0);

  const emitRef = useRef(emit);
  const enabledRef = useRef(enabled);
  const onAttemptRef = useRef(onAttempt);
  const getIncompletePatientNamesRef = useRef(getIncompletePatientNames);
  const scrollOptionsRef = useRef(scrollOptions);
  const handledScrollAttemptRef = useRef(submitAttemptCount);

  // Keep the subscription stable while letting the event handler read current render values.
  emitRef.current = emit;
  enabledRef.current = enabled;
  onAttemptRef.current = onAttempt;
  getIncompletePatientNamesRef.current = getIncompletePatientNames;
  scrollOptionsRef.current = scrollOptions;

  const resetSubmitAttemptCount = useCallback(() => {
    handledScrollAttemptRef.current = 0;
    setSubmitAttemptCount(0);
  }, []);

  useEffect(() => {
    if (resetWhen) {
      resetSubmitAttemptCount();
    }
  }, [resetSubmitAttemptCount, resetWhen]);

  useEffect(() => {
    const unsubscribe = subscribe<IContinueAttemptEvent>(
      OBEvents.continueAttempt,
      payload => {
        if (payload.currentStep !== step || !enabledRef.current) {
          return;
        }

        const incompletePatientNames = getIncompletePatientNamesRef.current?.();

        if (incompletePatientNames?.length) {
          emitRef.current<IIncompletePatientsCheckEvent>(
            OBEvents.incompletePatientsCheck,
            {
              currentStep: step,
              attemptId: payload.attemptId,
              patients: incompletePatientNames,
            },
          );
        }

        onAttemptRef.current?.(payload);
        setSubmitAttemptCount(count => count + 1);
      },
      {
        self: true,
      },
    );

    return () => unsubscribe();
  }, [step, subscribe]);

  useEffect(() => {
    if (submitAttemptCount === 0) {
      handledScrollAttemptRef.current = 0;
      return;
    }

    if (handledScrollAttemptRef.current === submitAttemptCount) {
      return;
    }

    handledScrollAttemptRef.current = submitAttemptCount;

    let animationFrame: number | undefined;
    let lookupAttempt = 0;
    let isActive = true;

    const scrollToFirstAvailableError = () => {
      if (!isActive) {
        return;
      }

      const scrollTarget = getFirstErrorScrollTarget();

      if (scrollTarget) {
        scrollTarget.scrollIntoView(scrollOptionsRef.current);
        return;
      }

      if (lookupAttempt >= MAX_SCROLL_LOOKUP_ATTEMPTS) {
        return;
      }

      lookupAttempt += 1;
      animationFrame = globalThis.requestAnimationFrame(
        scrollToFirstAvailableError,
      );
    };

    animationFrame = globalThis.requestAnimationFrame(
      scrollToFirstAvailableError,
    );

    return () => {
      isActive = false;

      if (animationFrame !== undefined) {
        globalThis.cancelAnimationFrame(animationFrame);
      }
    };
  }, [submitAttemptCount]);

  return {
    submitAttemptCount,
    resetSubmitAttemptCount,
  };
};

export default useContinueValidationFeedback;
